import { useCallback, useEffect, useMemo, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import {
  apiRequest,
  buildReportThemeSaveBody,
  getBootstrapData,
  ReportThemeEditorModal,
  type ReportThemeApiRow,
  type ReportThemeColors,
  type ReportThemeEditorApi,
  type ReportThemeEditorMode,
  type ReportThemeFontAssetRow,
  type ReportThemeFontSlot,
  type ReportThemeGoogleFontFamily,
} from '@tpct/admin-ui';
import FeatureGateNotice from '@/hub-shared/components/FeatureGateNotice';
import { __ } from '@/hub-shared/utils/i18n';
import { getMediaDisplayUrl } from '@/hub-shared/utils/mediaPayload';
import { throwIfReportFontUploadFailed } from '@/hub-shared/utils/reportFontUploadError';
import MediaUploader from '../../../companies/src/shared/MediaUploader';
import { AddItemIcon } from '../../../companies/src/shared/AddItemIcon';
import type { PortalGateDecision } from '../gates/portalGates';
import type { CabinetPageData } from '../types/cabinet-page';
import { parseApiError } from '../utils/parseApiError';
import { errorToUserFacingMessage } from '../api/apiErrors';

type ThemeApiRow = ReportThemeApiRow;

const getVsmeApiConfig = () => {
  const bs = getBootstrapData('AccountingHubPortal') as { restUrlVsme?: string; nonce: string };
  const base = (bs.restUrlVsme ?? '').replace(/\/?$/, '/');
  return { base, nonce: bs.nonce };
};

const vsmeRequest = async <T,>(method: string, path: string, body?: unknown): Promise<T> => {
  const { base, nonce } = getVsmeApiConfig();
  const res = await fetch(`${base}${path}`, {
    method,
    headers: { 'Content-Type': 'application/json', 'X-WP-Nonce': nonce },
    credentials: 'same-origin',
    body: body !== undefined ? JSON.stringify(body) : undefined,
  });
  if (!res.ok) {
    throw new Error(await parseApiError(res));
  }
  return res.json() as Promise<T>;
};

const uploadCabinetFontFile = async (file: File) => {
  const { base, nonce } = getVsmeApiConfig();
  const body = new FormData();
  body.append('file', file);
  const res = await fetch(`${base}report-fonts/cabinet`, {
    method: 'POST',
    headers: { 'X-WP-Nonce': nonce },
    credentials: 'same-origin',
    body,
  });
  if (!res.ok) {
    await throwIfReportFontUploadFailed(res, __);
  }
  const json = await res.json() as { data: { font: ReportThemeFontAssetRow } };
  return json.data.font;
};

const PencilIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const TrashIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <polyline points="3 6 5 6 21 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    <path d="M10 11v6M14 11v6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

type CabinetThemesTabProps = {
  data: CabinetPageData;
  queryKey: unknown[];
  bootstrapKey: string;
  cabinetProfileEditGate: PortalGateDecision;
};

const CabinetThemesTab = ({ data, queryKey, bootstrapKey, cabinetProfileEditGate }: CabinetThemesTabProps) => {
  const canEditCabinetProfile = cabinetProfileEditGate.allowed;
  const logoPreviewUrl = getMediaDisplayUrl(data.logo_media);
  const queryClient = useQueryClient();
  const [themes, setThemes] = useState<ThemeApiRow[]>([]);
  const [loadState, setLoadState] = useState<'loading' | 'ready' | 'error'>('loading');
  const [loadError, setLoadError] = useState('');
  const [modal, setModal] = useState<ReportThemeEditorMode | null>(null);
  const [logoSaveState, setLogoSaveState] = useState<'idle' | 'saving' | 'error'>('idle');
  const [logoError, setLogoError] = useState('');

  const themeEditorApi = useMemo<ReportThemeEditorApi>(
    () => ({
      listCompanyFonts: async () => {
        const res = await vsmeRequest<{ data: { fonts: ReportThemeFontAssetRow[] } }>(
          'GET',
          'report-fonts/cabinet',
        ).catch(() => ({ data: { fonts: [] as ReportThemeFontAssetRow[] } }));
        return res.data?.fonts ?? [];
      },
      listGoogleFamilies: async () => {
        const res = await vsmeRequest<{ data: { families: ReportThemeGoogleFontFamily[] } }>(
          'GET',
          'report-fonts/google-families',
        ).catch(() => ({ data: { families: [] as ReportThemeGoogleFontFamily[] } }));
        return res.data?.families ?? [];
      },
      uploadCompanyFont: async (_cid, file) => uploadCabinetFontFile(file),
    }),
    [],
  );

  const fetchCabinetTheme = useCallback(async () => {
    if (!canEditCabinetProfile) {
      setThemes([]);
      setLoadError('');
      setLoadState('ready');
      return;
    }

    setLoadState('loading');
    try {
      const res = await vsmeRequest<{ success: boolean; data: { theme: ThemeApiRow | null } }>(
        'GET',
        'report-themes/cabinet',
      );
      const theme = res.data?.theme ?? null;
      setThemes(theme ? [theme] : []);
      setLoadState('ready');
    } catch (e) {
      setLoadError(errorToUserFacingMessage(e, __('Chargement impossible.')));
      setLoadState('error');
    }
  }, [canEditCabinetProfile]);

  useEffect(() => {
    void fetchCabinetTheme();
  }, [fetchCabinetTheme]);

  const handleLogoChange = async (id: number | undefined) => {
    if (!canEditCabinetProfile) {
      return;
    }
    setLogoSaveState('saving');
    setLogoError('');
    try {
      const updatedData = await apiRequest<CabinetPageData>(
        'cabinet',
        { method: 'PATCH', body: JSON.stringify({ logo_attachment_id: id ?? null }) },
        bootstrapKey,
      );
      if (updatedData) {
        queryClient.setQueryData(queryKey, updatedData);
      }
      void queryClient.invalidateQueries({ queryKey });
      setLogoSaveState('idle');
    } catch (e) {
      setLogoSaveState('error');
      setLogoError(errorToUserFacingMessage(e, __('Erreur lors de la sauvegarde.')));
    }
  };

  const handleDelete = async (themeId: number) => {
    if (!canEditCabinetProfile) {
      return;
    }
    if (!window.confirm(__('Supprimer ce thème ?'))) return;
    try {
      await vsmeRequest('DELETE', `report-themes/${themeId}`);
      void fetchCabinetTheme();
    } catch (e) {
      window.alert(errorToUserFacingMessage(e, __('Erreur lors de la suppression.')));
    }
  };

  const handleModalSubmit = async (
    name: string,
    colors: ReportThemeColors,
    titleFont: ReportThemeFontSlot,
    bodyFont: ReportThemeFontSlot,
  ) => {
    if (!canEditCabinetProfile || modal === null) return;
    const body = buildReportThemeSaveBody(name, colors, titleFont, bodyFont);
    if (modal.kind === 'create') {
      await vsmeRequest('PUT', 'report-themes/cabinet', body);
    } else {
      await vsmeRequest('PUT', `report-themes/${modal.theme.id}`, body);
    }
    setModal(null);
    void fetchCabinetTheme();
  };

  const showAddCard = canEditCabinetProfile && themes.length === 0;

  return (
    <>
      <div className="themes-tab">
        {!canEditCabinetProfile ? (
          <FeatureGateNotice decision={cabinetProfileEditGate} className="themes-tab__gate-notice" />
        ) : null}
        {logoSaveState === 'error' ? <p className="themes-tab__error">{logoError}</p> : null}
        {canEditCabinetProfile && loadState === 'error' ? (
          <p className="themes-tab__error">{loadError}</p>
        ) : null}
        <section
          className="container-style themes-tab__section"
          aria-labelledby="cabinet-themes-logo-heading"
        >
          <h4 id="cabinet-themes-logo-heading">{__('Logo du cabinet')}</h4>
          <div className="themes-tab__logo-row">
            <div className="themes-tab__logo-column">
              {canEditCabinetProfile ? (
                <MediaUploader
                  value={data.logo_attachment_id ?? undefined}
                  media={data.logo_media}
                  onChange={(id) => { void handleLogoChange(id); }}
                  label={__('Logo')}
                  description={__('Le logo sera affiché sur les documents et rapports.')}
                  disabled={logoSaveState === 'saving'}
                  bootstrapKey={bootstrapKey}
                  uploadEndpoint="cabinet/upload-logo"
                />
              ) : logoPreviewUrl ? (
                <figure className="themes-tab__logo-readonly">
                  <img src={logoPreviewUrl} alt="" />
                  <figcaption>{__('Le logo sera affiché sur les documents et rapports.')}</figcaption>
                </figure>
              ) : (
                <p className="themes-tab__hint">{__('Aucun logo défini.')}</p>
              )}
            </div>
          </div>
        </section>

        <section className="container-style themes-tab__section" aria-labelledby="cabinet-themes-grid-heading">
          <h4 id="cabinet-themes-grid-heading">{__('Thème')}</h4>
          {canEditCabinetProfile && loadState === 'loading' && (
            <p className="themes-tab__hint">{__('Chargement…')}</p>
          )}
          {canEditCabinetProfile && loadState === 'ready' && (
            <div className="themes-tab__themes-grid">
              {themes.map((theme) => (
                <div key={theme.id} className="container-style themes-tab__theme-card">
                  <div className="themes-tab__theme-card-actions">
                    {theme.is_default && (
                      <span className="themes-tab__theme-default-badge">{__('Défaut')}</span>
                    )}
                    {canEditCabinetProfile ? (
                      <>
                        <button
                          type="button"
                          className="btn-icon themes-tab__action-btn"
                          onClick={() => setModal({ kind: 'edit', theme })}
                          aria-label={__('Modifier le thème')}
                        >
                          <PencilIcon />
                        </button>
                        <button
                          type="button"
                          className="btn-icon themes-tab__action-btn themes-tab__action-btn--delete"
                          onClick={() => { void handleDelete(theme.id); }}
                          aria-label={__('Supprimer le thème')}
                        >
                          <TrashIcon />
                        </button>
                      </>
                    ) : null}
                  </div>
                  <span
                    className="themes-tab__color-dot"
                    style={{ backgroundColor: theme.colors.main_dark }}
                    aria-hidden="true"
                  />
                  <p className="themes-tab__theme-label">{theme.name}</p>
                </div>
              ))}
              {showAddCard && (
                <button
                  type="button"
                  className="container-style themes-tab__theme-card themes-tab__add-card"
                  onClick={() => setModal({ kind: 'create' })}
                  aria-label={__('Créer un thème')}
                >
                  <AddItemIcon className="themes-tab__add-icon" />
                </button>
              )}
            </div>
          )}
        </section>
      </div>

      {canEditCabinetProfile && modal !== null && (
        <ReportThemeEditorModal
          mode={modal}
          companyId={0}
          api={themeEditorApi}
          t={__}
          onClose={() => setModal(null)}
          onSubmit={handleModalSubmit}
        />
      )}
    </>
  );
};

export default CabinetThemesTab;
